/**
 * Дизайн-токены Lens. Единый источник правды для всех цветов,
 * отступов и стилей. Импортируется в ConfigProvider (main.tsx)
 * и может использоваться напрямую в компонентах для inline-стилей.
 *
 * Цветовая палитра основана на mockups (hub.html, dictionaries.html).
 */

import { theme as antdAlgorithm } from 'antd';
import type { ThemeConfig } from 'antd';

/* ── Brand palette ───────────────────────────────────────────── */

export const colors = {
  // Primary
  primary: '#6C5CE7',
  primaryHover: '#5A4BD1',
  primaryBg: '#F0ECFF',
  primaryBgHover: '#E4DEFF',

  // Neutral
  bgLayout: '#F5F5F5',
  bgContainer: '#FFFFFF',
  bgHeader: '#1A1A2E',
  text: '#1A1A2E',
  textSecondary: '#888888',
  border: '#ECECEF',
  borderHover: '#6C5CE7',

  // Semantic
  success: '#27AE60',
  successBg: '#E8F8E8',
  warning: '#F39C12',
  warningBg: '#FEF5E7',
  error: '#E74C3C',
  errorBg: '#FDE8E8',
  info: '#2980B9',
  infoBg: '#E8F4FD',
  teal: '#0D9488',
  tealBg: '#E0F5F5',
} as const;

/* ── Spacing (кратно 4px) ────────────────────────────────────── */

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

/* ── Border radius ───────────────────────────────────────────── */

export const radius = {
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
  round: '50%',
} as const;

/* ── Shadows ─────────────────────────────────────────────────── */

export const shadows = {
  card: '0 1px 3px rgba(0, 0, 0, 0.04)',
  cardHover: '0 4px 12px rgba(108, 92, 231, 0.1)',
  dropdown: '0 6px 16px rgba(0, 0, 0, 0.08)',
} as const;

/* ── AntD theme config ───────────────────────────────────────── */

export const antdTheme: ThemeConfig = {
  token: {
    // Colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,
    colorBgLayout: colors.bgLayout,
    colorBgContainer: colors.bgContainer,
    colorText: colors.text,
    colorTextSecondary: colors.textSecondary,
    colorBorder: colors.border,

    // Typography — Roboto как основной, системный стек как fallback
    // (пока шрифт грузится / если субсет не подхватился).
    fontFamily:
      "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif",
    fontSize: 14,
    lineHeight: 1.5,

    // Border radius. Единый «почти прямой» язык: скругления читаются как
    // техническая деталь, а не как форма элемента. Круглыми остаются только
    // те места, где форма несёт смысл — аватары и точки-индикаторы
    // (radius.round).
    borderRadius: 3,
    borderRadiusLG: 4,
    borderRadiusSM: 2,

    // Motion
    motionDurationMid: '0.2s',

    // Sizing — компактнее «дефолтных» 40px для плотного, не «чанкового» вида
    controlHeight: 36,
    controlHeightSM: 30,
    controlHeightLG: 40,
  },
  components: {
    Button: {
      borderRadius: 3,
      fontWeight: 500,
      controlHeight: 36,
      controlHeightSM: 30,
    },
    Card: {
      borderRadiusLG: 4,
      paddingLG: 20,
    },
    Input: {
      borderRadius: 3,
      controlHeight: 36,
    },
    Select: {
      borderRadius: 3,
      controlHeight: 36,
    },
    Modal: {
      borderRadiusLG: 4,
      paddingContentHorizontalLG: 32,
    },
    Table: {
      headerBg: 'transparent',
      headerColor: colors.textSecondary,
      rowHoverBg: '#FAFAFE',
      borderColor: '#ECECEF',
      headerSplitColor: 'transparent',
      cellPaddingBlock: 9,
      cellPaddingInline: 12,
      cellFontSize: 13,
    },
    Tabs: {
      inkBarColor: colors.primary,
      itemSelectedColor: colors.primary,
      itemHoverColor: colors.text,
      itemColor: colors.textSecondary,
    },
    Layout: {
      headerBg: colors.bgHeader,
      headerHeight: 56,
      headerPadding: '0 24px',
      bodyBg: colors.bgLayout,
    },
    Tag: {
      borderRadiusSM: 3,
    },
    Breadcrumb: {
      fontSize: 14,
    },
  },
};

/* ── Status category colors ──────────────────────────────────── */

/**
 * Категорийные цвета статус-полей (select с hasCategories).
 * Единый источник правды, синхронный с северной звездой
 * mockups/redesign-v2.html (--c-backlog / --c-active / --c-done / --c-cancel).
 *
 * `color` — цвет «точки»/индикатора категории (= var(--c-*) из макета),
 * `bg` — мягкая подложка чипа (как .chip.* в макете).
 *
 * Используется как KanbanBoard (цвета колонок), так и любыми чипами
 * статусов в UI. Не дублировать локальными CATEGORY_COLORS.
 */
export const statusCategory = {
  backlog: { color: '#98A2B3', bg: '#EEF0F3' },
  active: { color: '#4C6FFF', bg: '#E8ECFF' },
  done: { color: '#18A957', bg: '#E3F6EC' },
  cancelled: { color: '#F0476A', bg: '#FDEBEF' },
} as const;

export type StatusCategoryKey = keyof typeof statusCategory;

/* ── Shell / rail (левая навигация-рельс) ────────────────────── */

/**
 * Узко-локальные токены оболочки (левый Sider-рельс).
 * Фон рельса переиспользует colors.bgHeader (#1A1A2E).
 * Прочие rail-* литералы живут в AppLayout.module.css —
 * здесь только значения, на которые могут ссылаться TS-компоненты.
 */
export const rail = {
  width: 248,
  bg: colors.bgHeader,
  itemHoverBg: 'rgba(255, 255, 255, 0.06)',
  itemActiveBg: 'rgba(108, 92, 231, 0.3)',
  textMuted: '#CFD2E6',
  sectionLabel: '#7F84A8',
} as const;

/* ── Role badge colors ───────────────────────────────────────── */

export const roleBadge = {
  admin: { bg: colors.infoBg, color: colors.info },
  member: { bg: colors.successBg, color: colors.success },
  viewer: { bg: '#F0F0F0', color: '#666666' },
} as const;

/* ── Тёмная тема (Pro/Dark) ──────────────────────────────────── */

/**
 * Тёмная палитра дизайн-языка «Pro». Светлая тема остаётся дефолтной и
 * не меняется; тёмная включается переключателем (ThemeProvider) и
 * опирается на antd darkAlgorithm + точечные override'ы ниже.
 */
export const darkColors = {
  primary: '#7C74FF',
  primaryHover: '#8E86FF',
  bgLayout: '#15151A',
  bgContainer: '#1C1C21',
  bgElevated: '#232329',
  bgHeader: '#121216',
  text: '#E6E6EA',
  textSecondary: '#8C8C96',
  textMuted: '#6C6C78',
  border: '#2A2A31',
  rowHover: '#222229',
  teal: '#19B8A6',
} as const;

const darkComponents: ThemeConfig['components'] = {
  ...antdTheme.components,
  Table: {
    headerBg: 'transparent',
    headerColor: darkColors.textSecondary,
    rowHoverBg: darkColors.rowHover,
    borderColor: darkColors.border,
    colorBgContainer: darkColors.bgContainer,
    headerSplitColor: 'transparent',
    cellPaddingBlock: 9,
    cellPaddingInline: 12,
    cellFontSize: 13,
  },
  Tabs: {
    inkBarColor: darkColors.primary,
    itemSelectedColor: darkColors.text,
    itemHoverColor: darkColors.text,
    itemColor: darkColors.textSecondary,
  },
  Layout: {
    headerBg: darkColors.bgHeader,
    headerHeight: 56,
    headerPadding: '0 24px',
    bodyBg: darkColors.bgLayout,
  },
  Tag: { borderRadiusSM: 3 },
};

export type ThemeMode = 'light' | 'dark';

/**
 * Масштабирует размеры шрифта в ThemeConfig под выбранный пользователем
 * базовый `fontSize`. AntD выводит производные размеры (SM/LG/заголовки) из
 * `token.fontSize` автоматически, но захардкоженные `fontSize`/`cellFontSize`
 * в components нужно пересчитать вручную, иначе таблицы и хлебные крошки не
 * масштабируются вместе с остальным интерфейсом.
 */
function withFontSize(config: ThemeConfig, fontSize: number): ThemeConfig {
  const scale = fontSize / 14; // 14 — базовый размер antdTheme
  const round = (n: number): number => Math.round(n * scale);
  const components = config.components ?? {};
  return {
    ...config,
    token: { ...config.token, fontSize },
    components: {
      ...components,
      Table: { ...components.Table, cellFontSize: round(13) },
      Breadcrumb: { ...components.Breadcrumb, fontSize: round(14) },
    },
  };
}

/**
 * Возвращает ThemeConfig для AntD по режиму и базовому размеру шрифта.
 * Светлая тема = antdTheme, тёмная = darkAlgorithm + override'ы; поверх обеих
 * применяется масштабирование шрифта (`fontSize`, дефолт 14 = без изменений).
 */
export function getAntdTheme(mode: ThemeMode, fontSize = 14): ThemeConfig {
  const base: ThemeConfig =
    mode === 'light'
      ? antdTheme
      : {
          algorithm: antdAlgorithm.darkAlgorithm,
          token: {
            ...antdTheme.token,
            colorPrimary: darkColors.primary,
            colorBgLayout: darkColors.bgLayout,
            colorBgContainer: darkColors.bgContainer,
            colorBgElevated: darkColors.bgElevated,
            colorText: darkColors.text,
            colorTextSecondary: darkColors.textSecondary,
            colorBorder: darkColors.border,
          },
          components: darkComponents,
        };
  return withFontSize(base, fontSize);
}

/* ── Палитра плиток пространств (аватар) ─────────────────────── */

/**
 * 10 цветов для solid-плитки аватара пространства. Один цвет на
 * пространство; если у space нет явного цвета — выбираем детерминированно
 * из id/названия (pickSpaceColor), чтобы он был стабилен между сессиями.
 * Белая иконка/буква читается на каждом (контраст проверен).
 */
export const spaceTilePalette = [
  '#6C5CE7', '#4C6FFF', '#3B82F6', '#0D9488', '#18A957',
  '#E8943A', '#D2574F', '#D4537E', '#8B5CF6', '#64748B',
] as const;

export function pickSpaceColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return spaceTilePalette[h % spaceTilePalette.length]!;
}
